import {
  Component,
  h,
  Element,
  Prop,
  State,
  Method,
  Event,
  EventEmitter,
  Watch,
  Host,
} from "@stencil/core"
import {
  dialogReducer,
  getInitialState,
  DialogState,
  DialogActions,
} from "./dialog-store"
import {
  StoreConnection,
  StateHandle,
  parseFunction,
  getOverlayOutletEl,
  modifyOverlayOutlet,
  animation,
} from "../../core/helpers/webcomponent-helpers"
import { bp } from "../../core/helpers/breakpoint-helpers"
import { Display, NotificationType } from "../../core/types/types"
import { IconName } from "../../core/types/icon-names"

@Component({
  tag: "sdx-dialog",
  styleUrl: "dialog.scss",
  shadow: true,
})
export class Dialog {
  private store: StoreConnection<this, DialogState, DialogActions>
  private componentEl?: HTMLElement
  private sdxBackdropEl?: HTMLSdxBackdropElement
  private animationDuration = 300
  private animationDurationMobile = 300
  private invokeDisplayChangeCallback: Function = () => null
  private invokeContentElHasDimensionsCallback: Function = () => null

  @Element() public el!: HTMLSdxDialogElement

  @State() public state!: StateHandle<DialogState>

  /**
   * Title that will be displayed in the header area.
   */
  @Prop() public label = ""

  /**
   * Defines how "persistent" the Modal is:
   * "modal" blocks the whole screen and forces the user to make a decision.
   * "closable-modal" is closable, does not block the screen and is therefore
   * ignorable. See Designers section for more details.
   */
  @Prop() public type: "modal" | "closable-modal" = "modal"

  /**
   * Callback that will fire after the modals display status has changed.
   * @deprecated use sdxdisplaychange event instead.
   */
  @Prop() public displayChangeCallback?: ((display: Display) => void) | string

  /**
   * Callback that will fire while "opening" when contentEl has display:block
   * and opacity:0. Needed for sdx-select.
   * @private
   */
  @Prop() public contentElHasDimensionsCallback?:
    | ((display: Display) => void)
    | string

  /**
   * Which icon to display on header area. Not respected when attribute
   * `notification-type` is used.
   */
  @Prop() public iconName?: IconName

  /**
   * Color of the icon on the header area (SDX predefined color class).
   * `white` if attribute `notification-type` is used.
   */
  @Prop() public iconColorClass = ""

  /**
   * The header is centred and displays a colour and icon matching the type of
   * notification. `alert` is deprecated.
   */
  @Prop() public notificationType?: NotificationType

  /**
   * The alignment of elements inside the header area. `true` if attribute
   * `notification-type` is used.
   */
  @Prop() public alignHeaderCenter = false

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated = true

  /**
   * Emitted when the component’s visibility updates.
   */
  @Event() public sdxdisplaychange!: EventEmitter<{
    display: "open" | "opening" | "closed" | "closing"
  }>

  @Watch("state")
  public stateChanged({}, prev: StateHandle<DialogState>) {
    const state = this.state.get()
    const prevState = prev.get()

    if (state.display !== prevState.display) {
      this.displayChanged(state)
    }
  }

  @Watch("notificationType")
  public notificationTypeChanged() {
    this.store.set("notificationType", this.notificationType)
    this.store.set("alignHeaderCenter", this.getAlignHeaderCenter())
    this.store.set("iconName", this.getIconName())
  }

  @Watch("label")
  public labelChanged() {
    this.store.set("label", this.label)
  }

  @Watch("type")
  public typeChanged() {
    this.store.set("type", this.type)
  }

  @Watch("displayChangeCallback")
  public displayChangeCallbackChanged() {
    this.setInvokeDisplayChangeCallback()
  }

  @Watch("iconName")
  public iconNameChanged() {
    this.store.set("iconName", this.getIconName())
  }

  @Watch("iconColorClass")
  public iconColorClassChanged() {
    this.store.set("iconColorClass", this.iconColorClass)
  }

  @Watch("alignHeaderCenter")
  public alignHeaderCenterChanged() {
    this.store.set("alignHeaderCenter", this.getAlignHeaderCenter())
  }

  /**
   * Toggles the modal.
   */
  @Method()
  public toggle(): Promise<void> {
    const { display } = this.state.get()

    if (display === "open") {
      return this.close()
    } else if (display === "closed") {
      return this.open()
    } else {
      // Ignore "opening" and "closing"
    }

    return Promise.resolve()
  }

  /**
   * Opens the modal.
   */
  @Method()
  public open(): Promise<void> {
    return new Promise((resolve) => {
      const { display, contentEl, isUserInteractionInProgress } =
        this.state.get()

      // Only a closed modal can be opened
      if (!(display === "closed" || display === "closing")) {
        resolve()
        return
      }

      this.setDisplay("opening", isUserInteractionInProgress)

      // Move modal to the top of all other elements, note that the modal is
      // appended to the backdrop for positioning
      modifyOverlayOutlet(
        "add",
        this.sdxBackdropEl!,
        getOverlayOutletEl(),
        this.el,
      )

      this.sdxBackdropEl!.append(contentEl!)

      // need to set display for sdx-select scrollSelectionIntoView() to work
      animation.set(contentEl!, { display: "block", opacity: "0" })
      this.invokeContentElHasDimensionsCallback()

      animation.set(contentEl!, {
        opacity: bp("sm") ? "0" : "1",
        scale: bp("sm") ? ".92" : "1",
        maxHeight: bp("sm") ? "none" : "0", // "auto" cannot be animated
      })

      animation.set(this.sdxBackdropEl!, { display: "block" })

      animation.start(
        this.sdxBackdropEl!,
        {
          backdropFilter: "blur(16px) brightness(90%)",
          webkitBackdropFilter: "blur(16px) brightness(90%)",
        },
        this.getAnimationDuration(),
      )

      animation.start(
        contentEl!,
        {
          ...(bp("sm")
            ? {
                opacity: "1",
                scale: "1",
              }
            : {
                maxHeight: `${window.innerHeight}px`,
              }),
        },
        this.getAnimationDuration(),
        {
          finished: () => {
            this.setDisplay("open", isUserInteractionInProgress)
            resolve()
          },
        },
      )
    })
  }

  /**
   * Closes the modal.
   */
  @Method()
  public close(): Promise<void> {
    return new Promise((resolve) => {
      const { display, contentEl, isUserInteractionInProgress } =
        this.state.get()

      // Only an opened modal can be closed
      if (display === "closed" || display === "closing") {
        resolve()
        return
      }

      this.setDisplay("closing", isUserInteractionInProgress)

      animation.start(
        this.sdxBackdropEl!,
        {
          backdropFilter: "blur(0px) brightness(100%)",
          webkitBackdropFilter: "blur(0px) brightness(100%)",
        },
        this.getAnimationDuration(),
      )

      animation.start(
        contentEl!,
        {
          ...(bp("sm")
            ? {
                opacity: "0",
                scale: ".92",
              }
            : {
                maxHeight: "0",
              }),
        },
        this.getAnimationDuration(),
        {
          finished: () => {
            animation.set(this.sdxBackdropEl!, { display: "none" })
            animation.set(contentEl!, { display: "none" })

            // Move modal back
            modifyOverlayOutlet("remove", contentEl!, this.el, this.el)
            this.componentEl!.append(this.sdxBackdropEl!)

            this.setDisplay("closed", isUserInteractionInProgress)

            resolve()
          },
        },
      )
    })
  }

  /**
   * Calls close(), but only when the component is allowed to be closed.
   * @private
   */
  @Method()
  public async requestToClose(
    e: KeyboardEvent | MouseEvent | TouchEvent,
  ): Promise<void> {
    const { contentEl } = this.state.get()

    if (!contentEl) {
      return
    }

    if (this.type !== "closable-modal") {
      return
    }

    if (e instanceof KeyboardEvent) {
      if (e.key === "Escape") {
        this.store.set("isUserInteractionInProgress", true)
        this.close()
        this.store.set("isUserInteractionInProgress", false)
      }

      return
    }

    const didClickOnContentEl = e.composedPath().includes(contentEl)

    if (didClickOnContentEl) {
      return
    }

    // Clicked somewhere else on window
    this.store.set("isUserInteractionInProgress", true)
    this.close()
    this.store.set("isUserInteractionInProgress", false)
  }

  /**
   * Get the current display state.
   * @private
   */
  @Method()
  public async getDisplay(): Promise<string> {
    return this.state.get().display
  }

  constructor() {
    this.setInvokeDisplayChangeCallback()
    this.invokeContentElHasDimensionsCallback = parseFunction(
      this.contentElHasDimensionsCallback,
    )

    this.store = new StoreConnection(this, dialogReducer, getInitialState(), [
      "display",
      "contentEl",
      "toggleEl",
    ])

    this.store.set("label", this.label)
    this.store.set("type", this.type)
    this.store.set("iconName", this.getIconName())
    this.store.set("iconColorClass", this.iconColorClass)
    this.store.set("toggle", this.toggle.bind(this))
    this.store.set("notificationType", this.notificationType)
    this.store.set("alignHeaderCenter", this.getAlignHeaderCenter())

    this.store.flush()
  }

  public disconnectedCallback() {
    // Make sure the dialog is closed when removed from the DOM
    this.close()
  }

  public componentDidLoad() {
    this.store.subscribe()
  }

  private displayChanged(state: DialogState) {
    this.invokeDisplayChangeCallback(state.display)
  }

  private setDisplay(
    display: Display,
    isUserInteractionInProgress: boolean,
  ): void {
    this.store.set("display", display)

    // Only fire events on end user interaction
    if (!isUserInteractionInProgress) {
      return
    }

    this.sdxdisplaychange.emit({ display })
  }

  private setInvokeDisplayChangeCallback(): void {
    this.invokeDisplayChangeCallback = parseFunction(this.displayChangeCallback)
  }

  private getAnimationDuration(): number {
    if (!this.animated) {
      return 0
    }

    return bp("sm") ? this.animationDuration : this.animationDurationMobile
  }

  private isValidNotificationType(value: unknown): value is NotificationType {
    return (
      value === "alert" ||
      value === "confirmation" ||
      value === "general" ||
      value === "warning"
    )
  }

  private getAlignHeaderCenter(): typeof this.alignHeaderCenter {
    return (
      this.alignHeaderCenter ||
      this.isValidNotificationType(this.notificationType)
    )
  }

  private getIconName(): IconName | undefined {
    switch (this.notificationType) {
      case "general":
        return "icon-information-circle-filled"

      case "confirmation":
        return "icon-check-mark-circle-filled"

      case "warning":
      case "alert":
        return "icon-exclamation-mark-circle-filled"

      default:
        return this.iconName
    }
  }

  public render() {
    const { toggleEl } = this.state.get()

    return (
      <Host class={{ "no-toggle": !toggleEl }}>
        <div class="component" ref={(el) => (this.componentEl = el)}>
          <slot />

          <sdx-backdrop ref={(el) => (this.sdxBackdropEl = el)} />
        </div>
      </Host>
    )
  }
}
